/**
 * What the Next Steps annotation write path actually costs, per session.
 *
 * Plan: `docs/plans/2026-09-08-next-steps-annotation-write-volume.md` §8.5.
 * Spec: the companion `-implementation-spec.md` §10.
 *
 * WHY THIS EXISTS, given that RUM already records every request. Datadog is
 * initialised with `trackResources: true` at `sessionSampleRate: 100`, so the
 * raw count of `PATCH /notes/:id` and `PUT /blank-answers` is already queryable
 * and is where the pre-change baseline comes from — this module is not needed
 * for that, and cannot be: all four phases of the plan ship in ONE store
 * release (decision 9.1), so no production build ever exists with the counter
 * and without the fix.
 *
 * What resource data cannot give, and this can:
 *
 *   1. REQUESTS PER EDITED ITEM. A resource query says "612 PATCHes"; it cannot
 *      say whether that was three notes or three hundred. That ratio is the
 *      headline number of the whole exercise, so `items` counts DISTINCT item
 *      ids beside `requests`.
 *   2. Queue health once the autosave engine lands — depth at `pause`, entries
 *      parked past the attempt cap, dead-key drops, conflicts each way. None
 *      of those have an HTTP footprint, and they are what an alert would fire
 *      on. The engine adds them to the same summary.
 *
 * ONE SUMMARY PER WINDOW, NOT ONE ACTION PER REQUEST. A RUM custom action per
 * write would double RUM volume for exactly the traffic being reduced, so
 * counts accumulate in memory and go out once — on `pause` and on logout, the
 * two moments a session reliably ends. A summary emitted at `pause` may not
 * leave the device until the next resume (the SDK batches and the webview is
 * frozen seconds later); that is expected, not loss.
 *
 * ITEM IDS NEVER LEAVE THE DEVICE. They are held in a Set only so it can be
 * sized; the payload carries `items[label] = set.size`. They are not content —
 * a note id is a uuid — but they are not needed either, and the smallest thing
 * that answers the question is the right thing to send.
 */
import { addDatadogRumAction } from '@/utils/datadog';

/** The RUM action name. One per window; queryable as `@action.name`. */
export const ANNOTATION_TELEMETRY_ACTION = 'courses.annotations.summary';

/**
 * Every counted write, and the one read the plan removes (`note:read-one` is
 * the Notes Hub's per-edit corpus GET, deleted by item 1a — so it is the
 * cleanest before/after pair in the release). Pure reads are deliberately NOT
 * counted: the corpus drain is high volume, unrelated to the write path, and
 * would bury the numbers this is for.
 */
export const ANNOTATION_REQUEST = Object.freeze({
  HIGHLIGHT_CREATE: 'highlight:create',
  HIGHLIGHT_UPDATE: 'highlight:update',
  HIGHLIGHT_DELETE: 'highlight:delete',
  NOTE_CREATE: 'note:create',
  NOTE_UPDATE: 'note:update',
  NOTE_DELETE: 'note:delete',
  NOTE_READ_ONE: 'note:read-one',
  NOTE_TAG_ATTACH: 'note:tag-attach',
  NOTE_TAG_DETACH: 'note:tag-detach',
  TAG_CREATE: 'tag:create',
  TAG_UPDATE: 'tag:update',
  TAG_DELETE: 'tag:delete',
  BLANK_UPSERT: 'blank:upsert',
  BLANK_DELETE: 'blank:delete',
});

/**
 * The queue-health events the engine, the drain and the store report (spec §10).
 * None of these has an HTTP footprint, and they are what an alert fires on: a
 * stranded write should be a metric, not a support ticket three weeks later.
 */
export const AUTOSAVE_EVENT = Object.freeze({
  /** A counted retry was scheduled (a transient server failure). */
  RETRY: 'retry',
  /** An uncounted retry was scheduled (no network, or the status could not be read). */
  RETRY_UNCOUNTED: 'retryUncounted',
  /** A row ran out of counted attempts and is parked. */
  PARKED: 'parked',
  /** A write was given up on; `detail.code` says why (an HTTP status or an error code). */
  DROPPED: 'dropped',
  /** `pause` ran; `detail.depth` is how many rows were unsent at that moment. */
  PAUSE: 'pause',
  /**
   * `pause` could not write a row to disk — its sealing refused, most often
   * because the enrolment status was never read and `pause` may not read it.
   * The row stayed in memory; only a kill before resume loses it.
   */
  PAUSE_PERSIST_FAILED: 'pausePersistFailed',
  /** The drain dropped a retained entry sealed under a key this device no longer holds. */
  OUTBOX_DROPPED_DEAD_KEY: 'outboxDroppedDeadKey',
  /** The age sweep evicted retained entries; `detail.count` says how many. */
  OUTBOX_DROPPED_AGE: 'outboxDroppedAge',
  /**
   * An entry queued before the member enrolled could not be sealed once they
   * had, so it could not be sent either. Expected to be ~0: the ordinary case
   * is that the drain seals it and sends it.
   */
  OUTBOX_DROPPED_PLAINTEXT: 'outboxDroppedPlaintext',
  /**
   * A replay the server answered with a verdict — the row is gone, the body is
   * refused — settled by the drain rather than replayed until the age cap.
   * `detail.count` says how many. The live engine's equivalent is `dropped`.
   */
  OUTBOX_DROPPED_REFUSED: 'outboxDroppedRefused',
  /**
   * A row the member was typing in was refused as stale, refetched, and sent
   * again with the fresh revision. Their version won.
   */
  CONFLICT_FORCED: 'conflictForced',
  /**
   * A replay from the outbox was refused as stale and removed without being
   * resent. The server's version won, and this device took it.
   */
  CONFLICT_YIELDED: 'conflictYielded',
});

/**
 * How many distinct ids per label are remembered. A member cannot edit more
 * items than this in one window (a whole program is ~199 blanks), so the size
 * is exact in practice; the cap is only here so a process that lives for days
 * on iOS cannot grow an unbounded Set. Requests stay exact either way.
 */
const ITEM_SAMPLE_CAP = 1000;

/** label -> number of requests attempted. */
const requests = new Map();
/** label -> Set of item ids seen. Sized on the way out, never sent. */
const itemIds = new Map();
/** When the first request of this window was counted, for a rate. */
let openedAt = null;

const emptyQueueHealth = () => ({
  retries: 0,
  uncountedRetries: 0,
  parked: 0,
  /** code -> how many writes were given up on for it. */
  dropped: {},
  /** The LAST pause's depth: a gauge, not a sum. `null` until a pause happens. */
  queueDepthAtPause: null,
  pausePersistFailed: 0,
  outboxDroppedDeadKey: 0,
  outboxDroppedAge: 0,
  outboxDroppedPlaintext: 0,
  outboxDroppedRefused: 0,
  conflictsForced: 0,
  conflictsYielded: 0,
});
/** The queue-health half of the window. */
let queue = emptyQueueHealth();
/** Whether anything at all was counted into `queue` this window. */
let queueTouched = false;

/**
 * Count one queue-health event. Never throws, for the same reason as
 * `countAnnotationRequest`: nothing here may fail a member's write.
 *
 * @param {string} event one of AUTOSAVE_EVENT
 * @param {{ code?: string|number, depth?: number, count?: number }} [detail]
 */
export function countAutosaveEvent(event, detail = {}) {
  try {
    switch (event) {
      case AUTOSAVE_EVENT.RETRY:
        queue.retries += 1;
        break;
      case AUTOSAVE_EVENT.RETRY_UNCOUNTED:
        queue.uncountedRetries += 1;
        break;
      case AUTOSAVE_EVENT.PARKED:
        queue.parked += 1;
        break;
      case AUTOSAVE_EVENT.DROPPED: {
        const code = String(detail?.code ?? 'unknown');
        queue.dropped[code] = (queue.dropped[code] || 0) + 1;
        break;
      }
      case AUTOSAVE_EVENT.PAUSE:
        queue.queueDepthAtPause = Number.isFinite(detail?.depth) ? detail.depth : 0;
        break;
      case AUTOSAVE_EVENT.PAUSE_PERSIST_FAILED:
        queue.pausePersistFailed += 1;
        break;
      case AUTOSAVE_EVENT.OUTBOX_DROPPED_DEAD_KEY:
        queue.outboxDroppedDeadKey += Number.isFinite(detail?.count) ? detail.count : 1;
        break;
      case AUTOSAVE_EVENT.OUTBOX_DROPPED_AGE:
        queue.outboxDroppedAge += Number.isFinite(detail?.count) ? detail.count : 1;
        break;
      case AUTOSAVE_EVENT.OUTBOX_DROPPED_PLAINTEXT:
        queue.outboxDroppedPlaintext += Number.isFinite(detail?.count) ? detail.count : 1;
        break;
      case AUTOSAVE_EVENT.OUTBOX_DROPPED_REFUSED:
        queue.outboxDroppedRefused += Number.isFinite(detail?.count) ? detail.count : 1;
        break;
      case AUTOSAVE_EVENT.CONFLICT_FORCED:
        queue.conflictsForced += 1;
        break;
      case AUTOSAVE_EVENT.CONFLICT_YIELDED:
        queue.conflictsYielded += 1;
        break;
      default:
        return;
    }
    // A pause with an empty queue is the ordinary session, not a signal; it
    // opens no window on its own.
    if (event !== AUTOSAVE_EVENT.PAUSE || queue.queueDepthAtPause > 0) queueTouched = true;
  } catch (error) {
    /* A counter is never worth an exception on the write path. */
  }
}

/**
 * Count one attempted request. Called at the point of no return — after any
 * sealing (a write refused by the sealing hook never reaches the wire and must
 * not be counted) and immediately before the axios call.
 *
 * Never throws: telemetry must not be able to fail a member's write.
 *
 * @param {string} label one of ANNOTATION_REQUEST
 * @param {string|null} [itemId] the row this write is for, when one is known
 */
export function countAnnotationRequest(label, itemId = null) {
  try {
    if (!label) return;
    if (openedAt === null) openedAt = Date.now();
    requests.set(label, (requests.get(label) || 0) + 1);
    if (!itemId) return;
    let seen = itemIds.get(label);
    if (!seen) {
      seen = new Set();
      itemIds.set(label, seen);
    }
    if (seen.size < ITEM_SAMPLE_CAP) seen.add(itemId);
  } catch (error) {
    /* A counter is never worth an exception on the write path. */
  }
}

/**
 * The payload for the current window, or `null` when nothing has been counted.
 * Exported for the spec and for a caller that wants to log it without emitting.
 *
 * @returns {{
 *   requests: Record<string, number>,
 *   items: Record<string, number>,
 *   totalRequests: number,
 *   windowMs: number,
 * } | null}
 */
export function annotationTelemetrySnapshot() {
  if (requests.size === 0 && !queueTouched) return null;
  const payload = {
    requests: {},
    items: {},
    totalRequests: 0,
    windowMs: 0,
    // Spelled out rather than spread, so the payload's shape is visible here and
    // a renamed field is a diff in this file, not a silent dashboard break.
    retries: queue.retries,
    uncountedRetries: queue.uncountedRetries,
    parked: queue.parked,
    dropped: { ...queue.dropped },
    queueDepthAtPause: queue.queueDepthAtPause,
    pausePersistFailed: queue.pausePersistFailed,
    outboxDroppedDeadKey: queue.outboxDroppedDeadKey,
    outboxDroppedAge: queue.outboxDroppedAge,
    outboxDroppedPlaintext: queue.outboxDroppedPlaintext,
    outboxDroppedRefused: queue.outboxDroppedRefused,
    conflictsForced: queue.conflictsForced,
    conflictsYielded: queue.conflictsYielded,
  };
  requests.forEach((count, label) => {
    payload.requests[label] = count;
    payload.totalRequests += count;
    const seen = itemIds.get(label);
    if (seen) payload.items[label] = seen.size;
  });
  payload.windowMs = openedAt === null ? 0 : Date.now() - openedAt;
  return payload;
}

/** Start a fresh window. Called after every emit. */
export function resetAnnotationTelemetry() {
  requests.clear();
  itemIds.clear();
  openedAt = null;
  queue = emptyQueueHealth();
  queueTouched = false;
}

/**
 * Send the window to RUM and start a new one. A window with nothing in it emits
 * nothing — most sessions never open Next Steps, and an empty summary from every
 * one of them is noise.
 *
 * Goes through `addDatadogRumAction` rather than `datadogRum` directly because
 * that helper already stands down when RUM is not initialised, which a `pause`
 * on a cold start can beat.
 *
 * @param {'pause'|'logout'|string} reason which moment ended the window
 * @returns {boolean} whether anything was sent
 */
export function emitAnnotationTelemetry(reason) {
  const payload = annotationTelemetrySnapshot();
  if (!payload) return false;
  resetAnnotationTelemetry();
  addDatadogRumAction(ANNOTATION_TELEMETRY_ACTION, { ...payload, reason });
  return true;
}
